import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * 底部抽屉。移动端最重要的一个容器——绝大多数"弹窗"都应该是它，而不是居中弹窗
 * （手指够不到屏幕中上部，这是单手持机的物理事实）。
 *
 * 做对一个抽屉需要的东西比看起来多：
 * - 跟手的下拉关闭（不跟手就完全不像原生）；
 * - 越拖越沉的阻尼（拖过头还线性跟随会显得很轻浮）；
 * - 速度判定（快速一甩就该关，不该要求拖够一半）；
 * - 内部可滚动，且滚到顶再下拉才触发关闭（否则内容滚不动）；
 * - 锁背景滚动 / 困住焦点 / Esc / 安卓返回键；
 * - 安全区内边距（不然底部按钮压在 Home Indicator 上）。
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useOverlay } from '../hooks/overlay.js';
const VELOCITY_TO_CLOSE = 0.6; // px/ms，快速下甩直接关
/**
 * 判定"这是拖拽而不是点击"的位移阈值（px）。
 * 手指按下时总会抖几个像素，阈值太小会把点击误判成拖拽。
 */
const DRAG_SLOP = 6;
export function Sheet({ open, onClose, title, children, footer, dismissRatio = 0.3, closeOnScrim = true, handle = true, maxHeightRatio = 0.9, className = '', }) {
    const panelRef = useRef(null);
    const scrollRef = useRef(null);
    const drag = useRef(null);
    const [offset, setOffset] = useState(0);
    const [dragging, setDragging] = useState(false);
    // 关闭时先播完退场动画再卸载，否则抽屉是"啪"地消失的
    const [mounted, setMounted] = useState(open);
    useEffect(() => {
        if (open) {
            setMounted(true);
            setOffset(0);
            return;
        }
        const t = setTimeout(() => setMounted(false), 240);
        return () => clearTimeout(t);
    }, [open]);
    useOverlay({ active: open, onClose, containerRef: panelRef });
    const onPointerDown = useCallback((e) => {
        if (e.button !== 0)
            return;
        // 内容还没滚到顶时，下拉应该是滚动内容，不是关抽屉。
        // 不做这条判断，长内容的抽屉会变成"往下一滑就关了，根本看不了"。
        const scroller = scrollRef.current;
        const fromHandle = e.target.closest('[data-ps-sheet-handle]') !== null;
        if (!fromHandle && scroller && scroller.scrollTop > 0)
            return;
        // ⚠️ **这里绝不能 setPointerCapture。**
        // 捕获会把后续的兼容 click 事件重定向到捕获元素，于是抽屉里的每一个按钮都点不动
        //（click 的 target 变成 .ps-sheet），而视觉上完全正常——一个底部抽屉里的按钮全失灵，
        // 是这个组件能出的最严重的故障，而它只在"真的去点"的时候才暴露。（已复现。）
        // 捕获推迟到 onPointerMove 里确认真的在拖之后再做：轻点永远不捕获，拖拽照样跟手。
        const now = performance.now();
        drag.current = { startY: e.clientY, startAt: now, lastY: e.clientY, lastAt: now, active: true, captured: false };
    }, []);
    const onPointerMove = useCallback((e) => {
        const d = drag.current;
        if (!d?.active)
            return;
        const dy = e.clientY - d.startY;
        d.lastY = e.clientY;
        d.lastAt = performance.now();
        // 往上拖 = 用户想看下面的内容，不是想拖抽屉。
        // 这时候必须**彻底放弃**这次拖拽，否则会同时发生"内容在滚"和"抽屉在橡皮筋"，
        // 看起来就像整个面板在抖。抽屉本来也没有"往上拖"这个语义。
        if (dy < -DRAG_SLOP) {
            d.active = false;
            if (d.captured)
                e.currentTarget.releasePointerCapture?.(e.pointerId);
            d.captured = false;
            setOffset(0);
            setDragging(false);
            return;
        }
        // 还没超过滑动阈值：当作一次点击，什么都不做，更不要捕获指针。
        if (!d.captured) {
            if (dy <= DRAG_SLOP)
                return;
            // 确认是拖拽了才捕获——这样手指滑出面板也还能收到 move，而轻点不受影响
            e.currentTarget.setPointerCapture?.(e.pointerId);
            d.captured = true;
            setDragging(true);
        }
        setOffset(Math.max(0, dy));
    }, []);
    const endDrag = useCallback((e) => {
        const d = drag.current;
        drag.current = null;
        if (d?.captured)
            e.currentTarget.releasePointerCapture?.(e.pointerId);
        setDragging(false);
        // 没进入拖拽 = 这是一次点击，不要吃掉它，也不要按"拖了 0 像素"去判定
        if (!d?.active || !d.captured)
            return;
        const height = panelRef.current?.offsetHeight ?? window.innerHeight;
        const dy = d.lastY - d.startY;
        const dt = Math.max(1, d.lastAt - d.startAt);
        const velocity = dy / dt;
        // 拖够了 **或** 甩得够快。只看距离的话，快速小幅下甩不会关，手感就是"迟钝"。
        if (dy > height * dismissRatio || velocity > VELOCITY_TO_CLOSE)
            onClose();
        else
            setOffset(0);
    }, [dismissRatio, onClose]);
    if (!mounted)
        return null;
    return (_jsxs("div", { className: `ps-sheet-root ${open ? 'ps-open' : 'ps-closing'}`, role: "presentation", children: [_jsx("div", { className: "ps-scrim", onClick: closeOnScrim ? onClose : undefined, "aria-hidden": "true" }), _jsxs("div", { ref: panelRef, className: `ps-sheet ${className}`.trim(), role: "dialog", "aria-modal": "true", "aria-label": typeof title === 'string' ? title : undefined, tabIndex: -1, style: {
                    transform: offset ? `translateY(${offset}px)` : undefined,
                    transition: dragging ? 'none' : undefined,
                    maxHeight: `calc(${maxHeightRatio * 100}dvh)`,
                }, onPointerDown: onPointerDown, onPointerMove: onPointerMove, onPointerUp: endDrag, onPointerCancel: endDrag, children: [handle && (_jsx("div", { className: "ps-sheet-handle-area", "data-ps-sheet-handle": "", children: _jsx("div", { className: "ps-sheet-handle" }) })), title && _jsx("div", { className: "ps-sheet-title", children: title }), _jsx("div", { ref: scrollRef, className: "ps-scroll ps-sheet-body", children: children }), footer && _jsx("div", { className: "ps-sheet-footer", children: footer }), _jsx("div", { className: "ps-sheet-safe" })] })] }));
}
export function ConfirmDialog({ open, title, description, confirmText = '确定', cancelText = '取消', destructive = false, busy = false, onConfirm, onCancel, }) {
    const panelRef = useRef(null);
    useOverlay({ active: open, onClose: onCancel, containerRef: panelRef });
    if (!open)
        return null;
    return (_jsxs("div", { className: "ps-dialog-root ps-open", role: "presentation", children: [_jsx("div", { className: "ps-scrim", onClick: destructive ? undefined : onCancel, "aria-hidden": "true" }), _jsxs("div", { ref: panelRef, className: "ps-dialog", role: "alertdialog", "aria-modal": "true", "aria-label": title, tabIndex: -1, children: [_jsx("p", { className: "ps-dialog-title", children: title }), description && _jsx("div", { className: "ps-dialog-desc", children: description }), _jsxs("div", { className: "ps-dialog-actions", children: [_jsx("button", { type: "button", className: "ps-dialog-btn", onClick: onCancel, disabled: busy, children: cancelText }), _jsx("button", { type: "button", className: `ps-dialog-btn ps-dialog-confirm ${destructive ? 'ps-danger' : ''}`.trim(), onClick: onConfirm, disabled: busy, children: confirmText })] })] })] }));
}
//# sourceMappingURL=sheet.js.map